import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import {Country} from "country-state-city";
import {useEffect, useMemo} from "react";
import {FormProvider, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import * as Yup from "yup";

import Button from "@/components/button/Button.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {PhoneField} from "@/components/form/fields/PhoneField.tsx";
import {SelectField} from "@/components/form/fields/SelectField/SelectField.tsx";
import {SwitchField} from "@/components/form/fields/SwitchField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField";
import {TimezoneSelectField} from "@/components/form/fields/TimezoneSelectField/TimezoneSelectField.tsx";
import {FormWarning} from "@/components/form/FormWarning.tsx";
import {RequiredFieldsNote} from "@/components/form/requiredFieldsNote/RequiredFieldsNote.tsx";
import {SelectOption} from "@/components/select/Select.types.ts";
import {GQL_ACCOUNTDETAILS_UPDATE, UpdateAccountDetailsResult} from "@/lib/queries/accounts";
import {Account} from "@/lib/types/accounts";
import {BillingData} from "@/lib/types/billing.ts";
import {ResponseStatus} from "@/lib/types/common";
import {BLACKLISTED_COUNTRIES, COUNTRIES_WITH_MANDATORY_STATES} from "@/pages/billing/payment-method/schemas.ts";

interface AccountDetailsFormValues {
  name?: string;
  phone?: string;
  address?: string;
  country: string;
  city?: string;
  zipcode?: string;
  additional_info?: string;
  billing_name?: string;
  billing_email?: string;
  billing_address_1?: string;
  billing_address_2?: string;
  timezone?: string;
  require_two_factor?: boolean;
  is_mobile_enabled?: boolean;
  enable_public_link_sharing?: boolean;
  disable_regular_login?: boolean;
  inactive_session_timeout?: number;
}

type AccountDetailsFormKeys = keyof AccountDetailsFormValues;

const FIVE_MINUTES = 5;
const FIVE_DAYS_IN_MIN = 7200;

// Helper functions to convert between seconds (API) and minutes (UI)
const secondsToMinutes = (seconds: number | undefined | null): number => {
  if (!seconds) return 0;
  return Math.round(seconds / 60);
};

const minutesToSeconds = (minutes: number | undefined | null): number => {
  if (!minutes) return 0;
  return minutes * 60;
};

const EditAccountDetailsSchema = Yup.object({
  country: Yup.string().required("This field cannot be blank."),
  city: Yup.string().optional(),
  zipcode: Yup.string().when("country", {
    is: (val?: string) => COUNTRIES_WITH_MANDATORY_STATES.includes(val || ""),
    then: () => Yup.string().required("This field is required."),
    otherwise: () => Yup.string().optional().default(""),
  }),
  name: Yup.string().optional(),
  phone: Yup.string().optional(),
  additional_info: Yup.string().optional(),
  billing_name: Yup.string().optional(),
  billing_email: Yup.string().email("Please enter a valid E-Mail address.").optional(),
  billing_address_1: Yup.string().optional(),
  billing_address_2: Yup.string().optional(),
  address: Yup.string().required("This field cannot be blank."),
  timezone: Yup.string().optional(),
  require_two_factor: Yup.boolean().optional(),
  is_mobile_enabled: Yup.boolean().optional(),
  enable_public_link_sharing: Yup.boolean().optional(),
  disable_regular_login: Yup.boolean().optional(),
  inactive_session_timeout: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? undefined : value))
    .typeError(`Please enter a number from ${FIVE_MINUTES} to ${FIVE_DAYS_IN_MIN}.`)
    .min(FIVE_MINUTES, `Minimum value is ${FIVE_MINUTES}`)
    .max(FIVE_DAYS_IN_MIN, `Maximum value is ${FIVE_DAYS_IN_MIN}`),
});

type AccountDetailsFormProps = {
  accountDetails: Account;
  billingData?: BillingData;
};

export const AccountDetailsForm = ({accountDetails, billingData}: AccountDetailsFormProps) => {
  const [updateAccountDetails, {loading}] = useMutation<UpdateAccountDetailsResult>(GQL_ACCOUNTDETAILS_UPDATE);
  const methods = useForm<AccountDetailsFormValues>({
    resolver: yupResolver<AccountDetailsFormValues>(EditAccountDetailsSchema),
    defaultValues: {
      name: accountDetails?.name,
      phone: accountDetails?.phone,
      address: accountDetails?.address,
      country: accountDetails?.country,
      city: accountDetails?.city,
      zipcode: accountDetails?.zipcode,
      additional_info: accountDetails.additional_info,
      billing_name: billingData?.billing_name || "",
      billing_email: billingData?.billing_email || "",
      billing_address_1: billingData?.address_1 || "",
      billing_address_2: billingData?.address_2 || "",
      timezone: accountDetails?.timezone,
      require_two_factor: accountDetails?.require_two_factor,
      is_mobile_enabled: accountDetails?.is_mobile_enabled,
      enable_public_link_sharing: accountDetails?.enable_public_link_sharing,
      disable_regular_login: accountDetails?.disable_regular_login ?? false,
      inactive_session_timeout: secondsToMinutes(accountDetails?.inactive_session_timeout),
    },
  });

  useEffect(() => {
    methods.reset({
      name: accountDetails?.name,
      phone: accountDetails?.phone,
      address: accountDetails?.address,
      country: accountDetails?.country,
      city: accountDetails?.city,
      zipcode: accountDetails?.zipcode,
      additional_info: accountDetails.additional_info,
      billing_name: billingData?.billing_name || "",
      billing_email: billingData?.billing_email || "",
      billing_address_1: billingData?.address_1 || "",
      billing_address_2: billingData?.address_2 || "",
      timezone: accountDetails?.timezone,
      require_two_factor: accountDetails?.require_two_factor,
      is_mobile_enabled: accountDetails?.is_mobile_enabled,
      enable_public_link_sharing: accountDetails?.enable_public_link_sharing,
      disable_regular_login: accountDetails?.disable_regular_login ?? false,
      inactive_session_timeout: secondsToMinutes(accountDetails?.inactive_session_timeout),
    });
  }, [accountDetails, billingData, methods]);

  const onSubmit = async (data: AccountDetailsFormValues) => {
    const apiData = {
      ...data,
      inactive_session_timeout: minutesToSeconds(data.inactive_session_timeout),
    };

    const resp = await updateAccountDetails({
      variables: {data: apiData},
    });

    if (resp.data?.response?.status === ResponseStatus.SUCCESS) {
      toast.success("Account details updated successfully.");
    } else {
      if (resp.data?.response?.message) {
        Object.entries(resp.data.response.message).forEach(([fieldName, messageList]) => {
          methods.setError(fieldName as AccountDetailsFormKeys, {message: (messageList as string[])[0]});
        });
      }
    }
  };

  const allCountries = useMemo(
    () => Country.getAllCountries().filter((country) => !BLACKLISTED_COUNTRIES.includes(country.isoCode)),
    []
  );
  const countryOptions = useMemo(() => allCountries.map((c) => ({label: c.name, value: c.isoCode})), [allCountries]);
  const selectedCountry = methods.watch("country");
  const postalCodeRequired = COUNTRIES_WITH_MANDATORY_STATES.includes(selectedCountry);
  const inactiveSessionTimeout = methods.watch("inactive_session_timeout");

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          methods.handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <div className="col-xl-6">
          <RequiredFieldsNote />
          <div className="row">
            <div className="col-lg-6">
              <InputField name="name" label="Company" />
            </div>
            <div className="col-lg-6">
              <PhoneField name="phone" label="Phone" />
            </div>
          </div>
          <div className="row my-4">
            <TextAreaField
              className="col-12"
              name="address"
              label="Address"
              helpText="This address will appear on invoices if no billing details are present."
              required={true}
            />
          </div>
          <div className="row mb-3">
            <div className="col">
              <SelectField name="country" label="Country" options={countryOptions as SelectOption[]} required={true} />
            </div>
            <div className="col">
              <InputField name="city" label="City" />
            </div>
            <div className="col">
              <InputField name="zipcode" placeholder="92545" label="Zip Code" required={postalCodeRequired} />
            </div>
          </div>
          {billingData && (
            <>
              <div className="row mb-3">
                <div className="col">
                  <InputField name="billing_name" label="Billing Name" />
                </div>
                <div className="col">
                  <InputField name="billing_email" type="email" label="Billing Email" />
                </div>
              </div>
              <div className="row pb-3">
                <div className="col">
                  <InputField name="billing_address_1" label="Billing Address Line 1" />
                </div>
              </div>
              <div className="row pb-3">
                <div className="col">
                  <InputField name="billing_address_2" label="Billing Address Line 2" />
                </div>
              </div>
            </>
          )}
          <div className="row pb-3">
            <div className="col">
              <TextAreaField
                name="additional_info"
                label="Additional Info"
                tooltip={"Text provided here will also be found on invoices (e.g. VAT, TaxID, etc.)"}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-6">
              <TimezoneSelectField name={"timezone"} />
            </div>
          </div>
          <div className="mt-4">
            <SwitchField
              name="require_two_factor"
              label="Require two-factor authentication (2FA) for all users."
              tooltip="To override or reset 2FA access for individual users, go to Settings > Users."
              helpText="2FA setup will take effect upon next login."
            />
          </div>

          <div className="mt-4">
            <SwitchField
              name="is_mobile_enabled"
              label="Enable Mobile App Access"
              helpText="Enable or Disable the ability to use Mobile App."
            />
          </div>

          <div className="mt-3">
            <SwitchField
              name="enable_public_link_sharing"
              label="Enable Public Link Sharing for Uptime Check Reports"
              helpText="Enable or Disable the ability to share a public link to the Check Analysis option that is available on a check."
            />
          </div>

          <Divider className="mt-4" />

          <h5 className="pulseTextBodyBold pulse-color-gray-900 mt-4 mb-3">Authentication Settings</h5>

          <div className="mt-3">
            <SwitchField
              name="disable_regular_login"
              label="Require SSO Login"
              helpText="Require all users to log in via SSO."
              disabled={!accountDetails.has_sso_configured && !accountDetails.disable_regular_login}
              tooltip="To configure or disable SSO, go to Settings > SSO."
            />
          </div>

          <div className="mt-3">
            <InputField
              name="inactive_session_timeout"
              label="Inactive Session Timeout (minutes)"
              type="number"
              helpText="Automatically log out users after this many minutes of inactivity."
            />
            {(inactiveSessionTimeout || 0) > 0 && (inactiveSessionTimeout || 0) < 60 && (
              <FormWarning
                title="Suboptimal settings detected:"
                warnings={[
                  "Setting the inactive session timeout for less than 60 minutes may result in a sub optimal user experience.",
                ]}
              />
            )}
          </div>

          <Divider className="mt-4" />

          <Button type="submit" className="mt-4" disabled={loading}>
            {loading ? "Saving Details..." : "Update Details"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AccountDetailsForm;
